import React, { useCallback, useState } from "react";
import UseContext from "hooks/useContext";
import Counter from "components/counter/Counter";
import SectionWrapper from "../components/SectionWrapper";
import {
    INVITED_PROFILE_TYPE,
    addInvitedProfile,
    countInvitedProfiles,
    isJobbizBackfillOn,
    toggleInvitedProfile,
} from "pages/createMission/utils/invitedProfiles";
import JobbizProfilesTab from "./invitationTabs/JobbizProfilesTab";
import PrivatePoolTab from "./invitationTabs/PrivatePoolTab";
import SelectionTab from "./invitationTabs/SelectionTab";
import JobbizBackfillSwitch from "./invitationTabs/JobbizBackfillSwitch";

const TAB = {
    JOBBIZ: "jobbiz",
    PRIVATE_POOL: "privatePool",
    SELECTION: "selection",
};

const Invitation = () => {
    const { data, setData, language } = UseContext();
    const label = (key, defaultValue) => language.getValue(`missionCreation.invitation.${key}`, defaultValue);
    const [activeTab, setActiveTab] = useState(TAB.JOBBIZ);
    const invitedProfiles = data.invitedProfiles;

    const handleToggle = useCallback(
        (profile) =>
            setData((prev) => ({ ...prev, invitedProfiles: toggleInvitedProfile(prev.invitedProfiles, profile) })),
        [setData]
    );

    const handleAdd = useCallback(
        (profile) =>
            setData((prev) => ({ ...prev, invitedProfiles: addInvitedProfile(prev.invitedProfiles, profile) })),
        [setData]
    );

    const handleBackfillChange = (checked) => setData((prev) => ({ ...prev, jobbizBackfill: checked }));

    const tabs = [
        {
            key: TAB.JOBBIZ,
            label: label("tabs.jobbiz", "Profils Jobbiz"),
            count: countInvitedProfiles(invitedProfiles, INVITED_PROFILE_TYPE.JOBBIZ),
        },
        {
            key: TAB.PRIVATE_POOL,
            label: label("tabs.privatePool", "Vivier privé"),
            count: countInvitedProfiles(invitedProfiles, INVITED_PROFILE_TYPE.PRIVATE_POOL),
        },
        {
            key: TAB.SELECTION,
            label: label("tabs.selection", "Sélection"),
            count: countInvitedProfiles(invitedProfiles),
        },
    ];

    const tabId = (key) => `mission-invitation-tab-${key}`;
    const panelId = (key) => `mission-invitation-panel-${key}`;

    return (
        <SectionWrapper
            title={label("title", "Re-travailler avec un profil ?")}
            description={label(
                "description",
                "Sélectionnez les profils à placer en priorité, parmi les profils Jobbiz et votre vivier. Uniquement les profils disponibles sont affichés."
            )}
        >
            {/* Local tabs: the repo's Tabs component navigates between routes. */}
            <div role="tablist" aria-label={label("tabs.label", "Profils à inviter")} className="flex flex-wrap gap-2">
                {tabs.map((tab) => {
                    const isActive = tab.key === activeTab;
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            role="tab"
                            id={tabId(tab.key)}
                            aria-selected={isActive}
                            aria-controls={panelId(tab.key)}
                            onClick={() => setActiveTab(tab.key)}
                            className={`flex items-center gap-2 rounded-md px-3 py-2 ft-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-300 ${
                                isActive ? "bg-gray-100 text-gray-900" : "text-gray-500 hover:bg-gray-50 hover:text-gray-700"
                            }`}
                        >
                            {tab.label}
                            <Counter value={tab.count} />
                        </button>
                    );
                })}
            </div>
            {/* Every tab stays mounted: its list, search and scroll survive a switch of tab. */}
            <div role="tabpanel" id={panelId(TAB.JOBBIZ)} aria-labelledby={tabId(TAB.JOBBIZ)} hidden={activeTab !== TAB.JOBBIZ}>
                <JobbizProfilesTab
                    isActive={activeTab === TAB.JOBBIZ}
                    invitedProfiles={invitedProfiles}
                    onToggle={handleToggle}
                />
            </div>
            <div
                role="tabpanel"
                id={panelId(TAB.PRIVATE_POOL)}
                aria-labelledby={tabId(TAB.PRIVATE_POOL)}
                hidden={activeTab !== TAB.PRIVATE_POOL}
            >
                <PrivatePoolTab
                    isActive={activeTab === TAB.PRIVATE_POOL}
                    invitedProfiles={invitedProfiles}
                    onToggle={handleToggle}
                    onAdd={handleAdd}
                />
            </div>
            <div
                role="tabpanel"
                id={panelId(TAB.SELECTION)}
                aria-labelledby={tabId(TAB.SELECTION)}
                hidden={activeTab !== TAB.SELECTION}
            >
                <SelectionTab
                    isActive={activeTab === TAB.SELECTION}
                    invitedProfiles={invitedProfiles}
                    onToggle={handleToggle}
                />
            </div>
            <JobbizBackfillSwitch checked={isJobbizBackfillOn(data)} onChange={handleBackfillChange} />
        </SectionWrapper>
    );
};

export default Invitation;
